// ─── Qué pasó con los correos de una campaña ─────────────────────────────────
//
// LA REGLA VIVE ACÁ Y NO EN LA PANTALLA porque la usan tres: la fila del
// historial, el detalle que se despliega y la franja con el acumulado. Escrita
// tres veces se desalinearía, y entonces la franja diría un porcentaje y la
// fila otro.
//
// LO QUE ESTE MÓDULO EXISTE PARA EVITAR es un cero tranquilizador donde
// corresponde «no lo sabemos». Los contadores los llena el webhook de Resend,
// que empezó a funcionar el 27-09-2026: las campañas anteriores NO TIENEN esos
// campos. Mostrar `0 entregados` en la campaña de mayo sería afirmar que nadie
// la recibió, y es exactamente la mentira inversa a la del 14 de marzo, cuando
// una campaña figuraba como enviada y treinta personas nunca la recibieron.
//
// `undefined` y `0` son DOS COSAS DISTINTAS, y esa distinción es todo el módulo.

/** Los contadores que llena el webhook. Se listan una vez porque de esta lista
 *  sale la decisión de si la campaña está medida o no. */
const CAMPOS_DEL_PROVEEDOR = [
  'deliveredCount',
  'openedCount',
  'clickedCount',
  'bouncedCount',
  'complainedCount',
] as const;

export interface CampanaMedible {
  sentCount?: number;
  failedCount?: number;
  deliveredCount?: number;
  openedCount?: number;
  clickedCount?: number;
  bouncedCount?: number;
  complainedCount?: number;
}

export interface MetricasCampana {
  /** Cuántos aceptó el proveedor. Aceptar NO es entregar. */
  enviados: number;
  /** Cuántos rechazó o no confirmó el proveedor en el momento del envío. */
  fallidos: number;
  /**
   * `false` cuando esta campaña se envió antes de que midiéramos entregas: no
   * llegó ni un evento y nunca llegará. La pantalla debe decir «sin datos», no
   * cero.
   */
  medida: boolean;
  /** `undefined` cuando `medida` es `false`. Nunca `0` en ese caso. */
  entregados: number | undefined;
  aperturas: number | undefined;
  clics: number | undefined;
  rebotes: number | undefined;
  quejas: number | undefined;
}

/**
 * ESTÁ MEDIDA SI LLEGÓ ALGÚN EVENTO, no si llegaron todos.
 *
 * La decisión es por CAMPAÑA y no por contador, y eso importa: una campaña con
 * `deliveredCount: 12` y `openedCount` ausente SÍ está medida, así que sus
 * aperturas son **cero de verdad** —nadie la abrió— y no «no sabemos». Al
 * revés, decidir campo por campo pondría «sin datos» en las aperturas de una
 * campaña que estamos midiendo perfectamente, y escondería justo el número
 * preocupante.
 */
export function metricasCampana(row: CampanaMedible): MetricasCampana {
  const medida = CAMPOS_DEL_PROVEEDOR.some((c) => typeof row[c] === 'number');
  const valor = (c: (typeof CAMPOS_DEL_PROVEEDOR)[number]): number | undefined =>
    medida ? (row[c] ?? 0) : undefined;

  return {
    enviados: row.sentCount ?? 0,
    fallidos: row.failedCount ?? 0,
    medida,
    entregados: valor('deliveredCount'),
    aperturas: valor('openedCount'),
    clics: valor('clickedCount'),
    rebotes: valor('bouncedCount'),
    quejas: valor('complainedCount'),
  };
}

/**
 * El porcentaje sobre los enviados, redondeado, o `undefined` cuando no hay
 * nada que dividir.
 *
 * SIN ENVIADOS NO HAY PORCENTAJE. Una campaña que falló entera tiene
 * `sentCount: 0`, y dividir por ahí da `NaN` o `Infinity` — que en pantalla se
 * lee como «NaN%» y hace dudar de todo el resto de la tabla.
 */
export function porcentaje(parte: number | undefined, total: number): number | undefined {
  if (parte === undefined || total <= 0) return undefined;
  return Math.round((parte / total) * 100);
}

/** Desde cuándo se mide qué pasa DESPUÉS del envío: el webhook de Resend entró
 *  en producción el 27-09-2026. Para lo anterior no hay eventos, y no los habrá
 *  nunca — no es que estén tardando. */
export const INICIO_DE_LA_MEDICION = new Date('2026-09-27T00:00:00.000Z');

export type RazonSinDatos =
  /** No salió ni un correo, así que no hay nada que medir. */
  | 'nada-salio'
  /** Se envió antes de que midiéramos. Nunca vamos a saber qué pasó. */
  | 'anterior-a-la-medicion'
  /** Recién enviada: los avisos del proveedor tardan unos minutos. */
  | 'todavia-no-llegan';

/**
 * POR QUÉ esta campaña no tiene datos — que no es un detalle cosmético: las
 * tres razones piden cosas distintas de quien mira. «Anterior a la medición» se
 * acepta y se olvida; «todavía no llegan» se vuelve a mirar en un rato; «nada
 * salió» es un problema que hay que atender.
 *
 * Decirle «se envió antes de que midiéramos» a una campaña que falló entera
 * HOY sería falso, y es justo la que hay que mirar.
 *
 * Sólo tiene sentido preguntarlo cuando `metricasCampana(...).medida` es
 * `false`.
 */
export function razonSinDatos(row: CampanaMedible & { sentAt?: Date }): RazonSinDatos {
  if ((row.sentCount ?? 0) === 0) return 'nada-salio';
  if (row.sentAt && row.sentAt.getTime() < INICIO_DE_LA_MEDICION.getTime()) {
    return 'anterior-a-la-medicion';
  }
  return 'todavia-no-llegan';
}

export interface AcumuladoCampanas extends MetricasCampana {
  /** Cuántas campañas entraron en la suma. Cero = ninguna está medida todavía. */
  campanasMedidas: number;
  /** Cuántas quedaron fuera por no tener datos, POR CUALQUIER MOTIVO. */
  campanasSinDatos: number;
  /**
   * Cuántas quedaron fuera POR CADA MOTIVO.
   *
   * NO SE PUEDE RESUMIR EN UNA SOLA FRASE. Decir «16 campañas son anteriores a
   * la medición» cuando una de ellas es de anoche y falló entera mete en el
   * saco de lo viejo justo la que hay que mirar. El desglose existe para que la
   * pantalla no afirme un motivo que no sabe.
   */
  sinDatosPorRazon: Record<RazonSinDatos, number>;
}

/**
 * La suma de las campañas MEDIDAS, para responder «¿cómo nos va?» sin abrir una
 * por una.
 *
 * LAS NO MEDIDAS NO ENTRAN NI EN EL NUMERADOR NI EN EL DENOMINADOR. Sumarlas
 * como cero hundiría la tasa de entrega con campañas de las que no sabemos
 * nada, y el número quedaría inservible justo mientras la mayoría del historial
 * es anterior al webhook. Cuántas quedaron fuera se devuelve aparte para que la
 * pantalla lo diga.
 */
export function acumuladoCampanas(rows: (CampanaMedible & { sentAt?: Date })[]): AcumuladoCampanas {
  // ARRANCAN EN `undefined`, NO EN CERO. Con un historial entero anterior al
  // webhook, arrancar en cero devolvería `{ medida: false, entregados: 0 }`: la
  // franja de hoy no lo dibuja porque mira `medida` primero, pero el objeto
  // afirma que se entregaron cero correos, y basta un lector futuro que no mire
  // `medida` para que la mentira salga a pantalla. El tipo no debe poder decir
  // algo que no sabemos.
  const total: AcumuladoCampanas = {
    enviados: 0,
    fallidos: 0,
    medida: false,
    entregados: undefined,
    aperturas: undefined,
    clics: undefined,
    rebotes: undefined,
    quejas: undefined,
    campanasMedidas: 0,
    campanasSinDatos: 0,
    sinDatosPorRazon: { 'nada-salio': 0, 'anterior-a-la-medicion': 0, 'todavia-no-llegan': 0 },
  };

  for (const row of rows) {
    const m = metricasCampana(row);
    if (!m.medida) {
      total.campanasSinDatos += 1;
      total.sinDatosPorRazon[razonSinDatos(row)] += 1;
      continue;
    }
    total.campanasMedidas += 1;
    total.medida = true;
    total.enviados += m.enviados;
    total.fallidos += m.fallidos;
    total.entregados = (total.entregados ?? 0) + (m.entregados ?? 0);
    total.aperturas = (total.aperturas ?? 0) + (m.aperturas ?? 0);
    total.clics = (total.clics ?? 0) + (m.clics ?? 0);
    total.rebotes = (total.rebotes ?? 0) + (m.rebotes ?? 0);
    total.quejas = (total.quejas ?? 0) + (m.quejas ?? 0);
  }

  return total;
}
